import React, { useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useUserContext from 'Context/UserContext';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import {
  displaySuccess,
  displayErrorMessage,
} from 'Helpers/FacetNotificationManager';
import useCollection from 'Hooks/useCollection';
import MetaTable from 'Components/UI/Meta/MetaTable';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const FeatureToggles = () => {
  const { userResource } = useUserContext();
  const { collection, setCollection } = useCollection();
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Feature Toggles',
    isPageView: true,
  });

  const theme = useTheme();
  useBackgroundColor(theme.palette.facet.lightGray);

  async function loadFeatureToggles() {
    try {
      const featureToggleCollection = await userResource
        .relationship('featureToggles')
        .link('related')
        .Get();

      setCollection(featureToggleCollection);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }

  useEffect(() => {
    if (userResource) {
      loadFeatureToggles();
    }
  }, [userResource]);

  const onEditSubmit = () => {
    displaySuccess('Feature toggle saved');
  };

  const onEditFailure = (err) => {
    displayErrorMessage(err, err.errors);
  };

  const onReset = () => {
    if (userResource) {
      loadFeatureToggles();
    }
  };

  if (!(userResource && collection)) {
    return <LoadingIndicator />;
  }

  return (
    <MetaTable
      collection={collection}
      tableMetaOverrideSetName="FeatureToggleTableOverride"
      hasMetaQueryForm
      QueryFormProps={{
        availableFilters: [
          {
            name: 'name',
            query: 'getFeatureToggles',
          },
          {
            name: 'description',
            query: 'getFeatureToggles',
          },
          {
            name: 'enabled',
            query: 'getFeatureToggles',
          },
          {
            name: 'activationStrategy',
            query: 'getFeatureToggles',
            overrideOptions: collection
              .meta()
              .field('activationStrategy')
              .options()
              .list()
              .map(option => ({
                name: option.attr('prompt'),
                value: option.attr('id'),
              })),
          },
          {
            name: 'groupName',
            query: 'getFeatureToggles',
            overrideOptions: collection
              .meta()
              .field('groupName')
              ?.options()
              ?.list()
              ?.map(option => ({
                name: option.attr('prompt'),
                value: option.attr('id'),
              })) || [],
          },
        ],
        defaultFilters: {
          enabled: {
            value: false,
          },
        },
        onReset,
      }}
      hasSortableColumns
      linkQueryParamName={'getFeatureToggles'}
      features={{
        edit: {
          type: 'dialog',
          metaOverrideSetName: 'FeatureToggleEditOverride',
          onFailure: onEditFailure,
          onSubmit: onEditSubmit,
        },
      }}
      {...rootNodeProps}
    />
  );
};

export default FeatureToggles;
